import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import {
  ArrowRight, Sparkles, Play, Check, ChevronDown, Phone, Calendar,
  RefreshCw, Users, Clock, MessageSquare, Bell, ShieldCheck, Bot,
  BarChart3, Activity, Zap, Star, Heart, HeartHandshake, Hand,
  Stethoscope, CalendarCheck, Target, BadgeCheck, TrendingUp, Euro,
  Smartphone, Monitor, Award, Crown, FileText, Download, Camera,
  Dumbbell, Move,
} from "lucide-react";
import SmartRecordsLandingSection from "../components/SmartRecordsLandingSection";

const TREATMENTS = [
  { icon: Stethoscope, label: "Prima visita fisioterapica" },
  { icon: HeartHandshake, label: "Seduta osteopatica" },
  { icon: Hand, label: "Massoterapia" },
  { icon: Activity, label: "Riabilitazione post-infortunio" },
  { icon: Heart, label: "Ginnastica posturale" },
  { icon: RefreshCw, label: "Trattamenti ricorrenti" },
  { icon: Target, label: "Pacchetti sedute" },
  { icon: CalendarCheck, label: "Follow-up paziente" },
];

const PAIN_POINTS = [
  { icon: Phone,       text: "Interrompi sedute per rispondere al telefono" },
  { icon: Bell,        text: "Pazienti che dimenticano l'appuntamento" },
  { icon: Calendar,    text: "Agenda piena di cambi e spostamenti" },
  { icon: MessageSquare, text: "Richiami manuali che fanno perdere tempo" },
  { icon: Clock,       text: "Giorni con buchi improvvisi" },
  { icon: Award,       text: "Gestione poco professionale percepita dal cliente" },
];

const SOLUTION_CARDS = [
  { icon: Smartphone,  title: "Prenotazioni online 24/7",     text: "I pazienti prenotano anche fuori orario, dal telefono, senza chiamarti.", color: "from-sky-500 to-blue-600" },
  { icon: Calendar,    title: "Agenda intelligente",          text: "Vista giornaliera, settimanale e per operatore. Ordinata e sempre aggiornata.", color: "from-emerald-500 to-teal-600" },
  { icon: MessageSquare, title: "Reminder WhatsApp automatici", text: "Riduci no-show e dimenticanze con promemoria 24h e 2h prima della seduta.", color: "from-teal-500 to-cyan-600" },
  { icon: Users,       title: "Gestione pazienti",            text: "Storico sedute, note cliniche, contatti e pacchetti in un unico posto.", color: "from-blue-500 to-indigo-600" },
  { icon: Monitor,     title: "Smart Site professionale incluso", text: "Una mini-pagina web elegante per farti trovare e prenotare online.", color: "from-sky-600 to-blue-700" },
  { icon: BarChart3,   title: "Report e analytics",           text: "Monitora fatturato, presenze, recall e performance dello studio.", color: "from-emerald-600 to-green-700" },
];

const FAQS = [
  { q: "È adatto anche a studio singolo?",  a: "Sì, SmartBooker funziona perfettamente sia per il professionista singolo sia per studi con più collaboratori. La configurazione richiede pochi minuti." },
  { q: "Posso usarlo con collaboratori?",    a: "Assolutamente. Puoi aggiungere più fisioterapisti, osteopati o collaboratori, con calendari separati o condivisi e permessi personalizzati." },
  { q: "Serve installare qualcosa?",          a: "No, SmartBooker è completamente cloud. Accedi da browser, tablet o smartphone — nessuna installazione, nessun server da gestire." },
  { q: "Posso importare i clienti da Excel?", a: "Sì, carichi un file Excel o CSV con l'anagrafica dei pazienti e in pochi secondi tutto è importato e pronto all'uso." },
  { q: "È adatto a fisioterapisti e osteopati?", a: "Sì, la piattaforma è pensata specificatamente per i professionisti del benessere posturale, incluse terapie manuali, riabilitazione e ginnastica posturale." },
];

// ============================================================
// Main Component
// ============================================================
const BenesserePosturaleLanding = () => {
  const navigate = useNavigate();
  const [sessionId] = useState(() => `bpl_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const scrollDepthRef = useRef({ seen50: false, seen90: false });
  const [openFaq, setOpenFaq] = useState(-1);
  const [roiSessions, setRoiSessions] = useState(3);
  const [roiFee, setRoiFee] = useState(60);

  const track = async (event, meta = {}) => {
    try {
      await axios.post(`${API}/public/landing/track`, {
        event_type: event, landing: "benessere-posturale", session_id: sessionId, meta
      });
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    // SEO meta tags (inline, since react-helmet is not installed)
    document.title = "Software per Fisioterapisti e Osteopati | SmartBooker";
    const setMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let tag = document.querySelector(`meta[${attr}="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attr, name);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    setMeta("description", "Prenotazioni online, agenda e reminder WhatsApp per fisioterapisti, osteopati e terapisti manuali. Un'unica piattaforma per gestire tutto lo studio. 14 giorni gratis.");
    setMeta("og:title", "Software per Fisioterapisti e Osteopati | SmartBooker", true);
    setMeta("og:description", "Gestisci prenotazioni, agenda, richiami e clienti da un'unica piattaforma professionale. Demo gratuita.", true);
    setMeta("og:image", "https://smartbooker.it/og-cover-fisio.png", true);
    setMeta("og:image:width", "1200", true);
    setMeta("og:image:height", "630", true);
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:image", "https://smartbooker.it/og-cover-fisio.png");
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://smartbooker.it/benessere-posturale");

    track("page_view", { ref: document.referrer || "" });
    const onScroll = () => {
      const pct = (window.scrollY + window.innerHeight) / document.body.scrollHeight * 100;
      if (pct >= 50 && !scrollDepthRef.current.seen50) { scrollDepthRef.current.seen50 = true; track("scroll_50"); }
      if (pct >= 90 && !scrollDepthRef.current.seen90) { scrollDepthRef.current.seen90 = true; track("scroll_90"); }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
    // eslint-disable-next-line
  }, []);

  const goDemo = (source) => { track("demo_request", { source }); navigate("/demo-benessere-posturale"); };
  const goTrial = (source) => { track("hero_cta_click", { source, dest: "trial" }); navigate("/onboarding?sector=fisioterapia"); };
  const heroClick = (dest) => { track("hero_cta_click", { dest }); dest === "trial" ? goTrial("hero") : goDemo("hero"); };
  const pricingClick = (plan = "growth") => {
    track("pricing_click", { plan });
    navigate(`/onboarding?sector=fisioterapia&plan=${plan}`);
  };

  const monthlyLost = roiSessions * 4 * roiFee; // 4 weeks
  const yearlyLost = monthlyLost * 12;
  const fmtIT = (n) => {
    const manual = String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    try {
      const intl = new Intl.NumberFormat("it-IT").format(n);
      // In some headless/reduced-ICU environments Intl returns no separator.
      // Fall back to manual formatter when we detect a plain numeric string.
      return /^\d+$/.test(intl) ? manual : intl;
    } catch {
      return manual;
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="benessere-posturale-landing">
      {/* ================= NAV ================= */}
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-bold text-lg">
            <img src="/brand/sb-icon.png" alt="SmartBooker" className="h-9 w-9 drop-shadow-[0_0_10px_rgba(16,185,129,0.3)]" />
            <span className="bg-gradient-to-r from-sky-600 to-emerald-600 bg-clip-text text-transparent">SmartBooker</span>
            <span className="hidden sm:inline-block text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 ml-1 font-medium">Fisio · Osteo</span>
          </Link>
          <div className="hidden md:flex items-center gap-3">
            <button onClick={() => goDemo("nav")} className="text-sm text-slate-600 hover:text-slate-900 transition-colors font-medium" data-testid="nav-demo">Demo</button>
            <button onClick={() => goTrial("nav")} className="px-5 py-2 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white text-sm font-semibold shadow-md shadow-sky-500/20 transition-all hover:scale-[1.02]" data-testid="nav-cta-trial">
              Prova gratis
            </button>
          </div>
        </div>
      </nav>

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden pt-12 pb-20 sm:pt-20 sm:pb-32 px-5" data-testid="hero-section">
        {/* Soft medical gradient bg */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full bg-sky-100/60 blur-3xl" />
          <div className="absolute top-40 left-0 w-[500px] h-[500px] rounded-full bg-emerald-100/50 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white border border-slate-200 shadow-sm text-xs font-semibold text-slate-700 mb-6" data-testid="hero-badge">
              <Stethoscope className="w-3.5 h-3.5 text-emerald-600" />
              SmartBooker per Fisioterapisti e Osteopati
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] mb-5 tracking-tight">
              Più appuntamenti confermati.<br/>
              Meno assenze.<br/>
              <span className="bg-gradient-to-r from-sky-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">Più tempo per i pazienti.</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-600 leading-relaxed mb-8 max-w-xl">
              Gestisci prenotazioni online, agenda, richiami automatici e clienti da un'unica piattaforma professionale pensata per il tuo studio.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <Button
                onClick={() => heroClick("trial")}
                className="rounded-full px-8 py-7 text-base font-bold bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white shadow-lg shadow-emerald-500/30 hover:scale-[1.02] transition-all"
                data-testid="hero-cta-trial"
              >
                Prova demo gratuita
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button
                onClick={() => heroClick("demo")}
                variant="outline"
                className="rounded-full px-6 py-7 text-base font-semibold border-slate-300 hover:border-slate-400 hover:bg-slate-50"
                data-testid="hero-cta-demo"
              >
                <Play className="w-4 h-4 mr-2" /> Guarda come funziona
              </Button>
            </div>

            {/* Patient portal live preview — touch from your phone */}
            <a
              href="/portal/demo-portal-marco-rossi-recovery-2026"
              target="_blank" rel="noreferrer"
              onClick={() => track && track("hero_patient_portal_click", { source: "hero" })}
              className="group inline-flex items-center gap-3 mb-8 px-4 py-3 rounded-2xl bg-gradient-to-r from-teal-50 to-emerald-50 border border-teal-200 hover:border-teal-400 hover:shadow-md transition-all"
              data-testid="hero-patient-portal-cta"
            >
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-md">
                <Smartphone className="w-4 h-4 text-white" />
              </div>
              <div className="text-left">
                <p className="text-[10px] uppercase tracking-[0.16em] font-bold text-teal-700">Patient Portal · LIVE demo</p>
                <p className="text-sm font-bold text-slate-900 group-hover:text-teal-700 transition-colors">Apri l'esperienza paziente sul tuo smartphone →</p>
              </div>
            </a>

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-600">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Nessuna installazione</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Attivo in pochi minuti</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-emerald-500" /> Assistenza italiana</span>
            </div>
          </div>

          {/* Hero visual — laptop + phone mockup */}
          <div className="relative" data-testid="hero-visual">
            <div className="relative bg-gradient-to-br from-sky-50 to-emerald-50 rounded-3xl p-6 sm:p-10 shadow-2xl shadow-emerald-500/10 border border-white">
              {/* Laptop card */}
              <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-5 border border-slate-100">
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
                      <Calendar className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold">Agenda · Oggi</p>
                      <p className="text-[10px] text-slate-500">Martedì 4 Novembre</p>
                    </div>
                  </div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 font-semibold">8 sedute</span>
                </div>
                <div className="space-y-2">
                  {[
                    { time: "09:00", name: "Maria R.", treat: "Seduta osteopatica", color: "bg-sky-50 text-sky-700 border-sky-200" },
                    { time: "10:30", name: "Luigi F.", treat: "Riabilitazione spalla", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
                    { time: "11:30", name: "Sara T.", treat: "Ginnastica posturale", color: "bg-teal-50 text-teal-700 border-teal-200" },
                    { time: "15:00", name: "Marco B.", treat: "Prima visita fisio", color: "bg-blue-50 text-blue-700 border-blue-200" },
                  ].map((a, i) => (
                    <div key={i} className={`flex items-center gap-3 p-2.5 rounded-lg border ${a.color}`}>
                      <span className="text-xs font-bold w-12">{a.time}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{a.name}</p>
                        <p className="text-xs opacity-75 truncate">{a.treat}</p>
                      </div>
                      <BadgeCheck className="w-4 h-4 flex-shrink-0" />
                    </div>
                  ))}
                </div>
              </div>

              {/* Floating WhatsApp reminder card */}
              <div className="absolute -bottom-6 -right-2 sm:-right-4 bg-white rounded-2xl shadow-2xl border border-slate-100 p-3.5 w-64 sm:w-72">
                <div className="flex items-start gap-2.5">
                  <div className="w-9 h-9 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0">
                    <MessageSquare className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-slate-900">Reminder inviato</p>
                    <p className="text-[11px] text-slate-500 mt-0.5">Ciao Maria! Ti ricordiamo la seduta di domani alle 09:00 da Studio Benessere.</p>
                    <div className="flex items-center gap-1 mt-1.5 text-[10px] text-emerald-600 font-semibold">
                      <Check className="w-3 h-3" /> Confermata via WhatsApp
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= PAIN POINTS ================= */}
      <section className="py-20 px-5 bg-slate-50" data-testid="pain-points-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">Ti riconosci in queste situazioni?</h2>
            <p className="text-slate-600 text-lg">Problemi quotidiani che rubano tempo, energia e denaro al tuo studio.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {PAIN_POINTS.map((p, i) => (
              <div key={i} className="group bg-white rounded-2xl p-5 border border-slate-200 hover:border-rose-200 hover:shadow-md transition-all" data-testid={`pain-${i}`}>
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0 group-hover:bg-rose-100 transition-colors">
                    <p.icon className="w-5 h-5 text-rose-600" />
                  </div>
                  <p className="text-sm font-medium text-slate-700 leading-relaxed">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-10 p-6 rounded-2xl bg-gradient-to-br from-sky-600 to-emerald-600 text-white text-center shadow-xl">
            <p className="text-lg font-bold">SmartBooker nasce per risolvere tutto questo.</p>
          </div>
        </div>
      </section>

      {/* ================= SOLUTION CARDS ================= */}
      <section className="py-20 sm:py-28 px-5" data-testid="solution-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14 max-w-2xl mx-auto">
            <span className="inline-block text-xs font-bold tracking-widest text-emerald-600 uppercase mb-3">La soluzione</span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-4">Lo studio lavora meglio. Tu lavori meglio.</h2>
            <p className="text-slate-600 text-lg">Sei funzioni chiave che automatizzano tutto ciò che oggi ti porta via tempo.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SOLUTION_CARDS.map((c, i) => (
              <div key={i} className="group relative bg-white rounded-3xl p-7 border border-slate-200 hover:shadow-2xl hover:-translate-y-1 hover:border-transparent transition-all overflow-hidden" data-testid={`solution-${i}`}>
                <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${c.color}`} />
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${c.color} flex items-center justify-center mb-4 shadow-md`}>
                  <c.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-bold text-lg mb-2">{c.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= TREATMENTS ================= */}
      <section className="py-20 sm:py-24 px-5 bg-gradient-to-br from-sky-50/50 via-white to-emerald-50/50" data-testid="treatments-section">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block text-xs font-bold tracking-widest text-sky-600 uppercase mb-3">Per ogni trattamento</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">Perfetto per trattamenti su appuntamento</h2>
            <p className="text-slate-600 text-lg">Dalla prima visita al follow-up, SmartBooker gestisce ogni tipo di seduta.</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {TREATMENTS.map((t, i) => (
              <div key={i} className="flex flex-col items-center text-center p-5 bg-white rounded-2xl border border-slate-200 hover:border-emerald-300 hover:shadow-md transition-all" data-testid={`treatment-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center mb-3">
                  <t.icon className="w-6 h-6 text-emerald-700" />
                </div>
                <p className="text-sm font-semibold text-slate-800 leading-tight">{t.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= RECALL PAZIENTI ================= */}
      <section className="py-20 sm:py-28 px-5" data-testid="recall-section">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block text-xs font-bold tracking-widest text-emerald-600 uppercase mb-3">Recupero pazienti</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 leading-tight">
              Molti pazienti smettono solo perché <span className="text-rose-600">nessuno li richiama</span>.
            </h2>
            <p className="text-slate-600 text-lg mb-6 leading-relaxed">
              SmartBooker ti aiuta a ricordare controlli, cicli di sedute e ritorni periodici — senza sforzo manuale.
            </p>
            <ul className="space-y-3">
              {[
                { icon: RefreshCw, text: "Recall automatici dopo N giorni dall'ultima seduta" },
                { icon: Bell, text: "Reminder controlli periodici e check-up" },
                { icon: MessageSquare, text: "Messaggi WhatsApp personalizzati e professionali" },
                { icon: Users, text: "Follow-up mirati per pazienti inattivi" },
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <f.icon className="w-4.5 h-4.5 text-emerald-600" />
                  </div>
                  <span className="text-slate-700 pt-1.5 font-medium">{f.text}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl p-8 text-white shadow-2xl">
            <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-5 border border-white/30 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Bot className="w-5 h-5" />
                <span className="text-sm font-bold">SmartBooker Recall</span>
              </div>
              <p className="text-sm opacity-95 leading-relaxed">
                "Ciao Giulia! Sono passati 3 mesi dalla tua ultima seduta. Vuoi prenotare un controllo posturale?"
              </p>
              <div className="flex gap-2 mt-3">
                <span className="text-[10px] px-2 py-1 rounded-full bg-white/25 font-semibold">Prenota ora</span>
                <span className="text-[10px] px-2 py-1 rounded-full bg-white/25 font-semibold">Non ora</span>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="opacity-80">Tasso di conversione medio</span>
              <span className="text-2xl font-black">+22%</span>
            </div>
          </div>
        </div>
      </section>

      {/* ================= IMMAGINE PROFESSIONALE ================= */}
      <section className="py-20 sm:py-28 px-5 bg-slate-50" data-testid="image-section">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <div className="order-2 lg:order-1">
            <div className="bg-white rounded-3xl shadow-2xl p-6 border border-slate-100">
              <div className="relative h-44 rounded-2xl overflow-hidden mb-4 bg-gradient-to-br from-sky-400 to-emerald-500">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.3),transparent_60%)]" />
                <div className="absolute bottom-3 left-3 right-3 text-white">
                  <p className="text-[10px] uppercase tracking-widest opacity-80">Studio Benessere</p>
                  <p className="text-xl font-black">La tua Smart Page</p>
                </div>
              </div>
              <div className="space-y-2.5">
                <div className="flex items-center justify-between p-2.5 rounded-xl border border-slate-200">
                  <span className="text-sm font-medium">Prima visita fisioterapica</span>
                  <span className="text-sm font-bold text-emerald-600">€60</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-xl border border-slate-200">
                  <span className="text-sm font-medium">Seduta osteopatica</span>
                  <span className="text-sm font-bold text-emerald-600">€70</span>
                </div>
                <div className="flex items-center justify-between p-2.5 rounded-xl border border-slate-200">
                  <span className="text-sm font-medium">Massoterapia</span>
                  <span className="text-sm font-bold text-emerald-600">€50</span>
                </div>
              </div>
              <button className="w-full mt-4 py-3 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 text-white font-bold text-sm shadow-lg">
                Prenota online
              </button>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-block text-xs font-bold tracking-widest text-sky-600 uppercase mb-3">Immagine professionale</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-4 leading-tight">
              La percezione conta quanto il trattamento.
            </h2>
            <p className="text-slate-600 text-lg mb-4 leading-relaxed">
              Quando un paziente trova una pagina moderna, prenota facilmente e riceve conferme automatiche, percepisce uno studio <span className="font-bold text-slate-900">organizzato e affidabile</span>.
            </p>
            <p className="text-slate-600 leading-relaxed">
              La Smart Page è inclusa in tutti i piani: zero costi aggiuntivi, zero configurazioni tecniche.
            </p>
          </div>
        </div>
      </section>

      {/* ================= ROI CALCULATOR ================= */}
      <section className="py-20 sm:py-28 px-5" data-testid="roi-section">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block text-xs font-bold tracking-widest text-rose-600 uppercase mb-3">Quanto ti costano i no-show?</span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">
              Quanti soldi perdi ogni mese per appuntamenti saltati?
            </h2>
          </div>
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-3xl p-6 sm:p-10 text-white shadow-2xl">
            <div className="grid sm:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Sedute saltate a settimana</label>
                <div className="flex items-center gap-3">
                  <input type="range" min="1" max="10" value={roiSessions} onChange={e => setRoiSessions(parseInt(e.target.value))}
                    className="flex-1 accent-emerald-400" data-testid="roi-sessions-slider" />
                  <span className="text-3xl font-black text-emerald-400 w-12 text-center">{roiSessions}</span>
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Costo medio seduta (€)</label>
                <div className="flex items-center gap-3">
                  <input type="range" min="30" max="150" step="5" value={roiFee} onChange={e => setRoiFee(parseInt(e.target.value))}
                    className="flex-1 accent-sky-400" data-testid="roi-fee-slider" />
                  <span className="text-3xl font-black text-sky-400 w-16 text-center">€{roiFee}</span>
                </div>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 pt-6 border-t border-slate-700">
              <div className="p-5 rounded-2xl bg-rose-500/10 border border-rose-500/30">
                <p className="text-xs uppercase tracking-wider text-rose-300 font-bold mb-1">Perdi ogni mese</p>
                <p className="text-4xl font-black text-rose-400">€{fmtIT(monthlyLost)}</p>
                <p className="text-xs text-slate-400 mt-1">{roiSessions} sedute × 4 settimane × €{roiFee}</p>
              </div>
              <div className="p-5 rounded-2xl bg-rose-500/20 border border-rose-500/50">
                <p className="text-xs uppercase tracking-wider text-rose-200 font-bold mb-1">Perdi ogni anno</p>
                <p className="text-4xl font-black text-white">€{fmtIT(yearlyLost)}</p>
                <p className="text-xs text-slate-400 mt-1">Costo annuale dei no-show</p>
              </div>
            </div>
            <div className="mt-6 p-5 rounded-2xl bg-gradient-to-r from-sky-500 to-emerald-500 flex items-center justify-between flex-col sm:flex-row gap-4">
              <div>
                <p className="text-sm font-semibold opacity-90">Meglio prevenirli automaticamente.</p>
                <p className="text-xs opacity-80 mt-0.5">Reminder WhatsApp + conferme automatiche = -60% no-show</p>
              </div>
              <Button onClick={() => goTrial("roi")} className="rounded-full bg-white text-slate-900 hover:bg-slate-100 font-bold px-6 whitespace-nowrap" data-testid="roi-cta">
                Inizia gratis <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ================= TESTIMONIAL ================= */}
      <section className="py-20 sm:py-24 px-5 bg-gradient-to-br from-sky-50 to-emerald-50" data-testid="testimonial-section">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-block text-xs font-bold tracking-widest text-emerald-600 uppercase mb-3">Testimonianza</div>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-10">Chi lavora bene merita strumenti all'altezza.</h2>
          <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl border border-white">
            <div className="flex justify-center gap-1 mb-5">
              {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />)}
            </div>
            <blockquote className="text-xl sm:text-2xl font-medium text-slate-800 leading-relaxed mb-6">
              "Prima passavo tempo tra chiamate e messaggi. Ora l'agenda si riempie da sola e i pazienti dimenticano meno appuntamenti."
            </blockquote>
            <div className="flex items-center justify-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-white" />
              </div>
              <div className="text-left">
                <p className="text-sm font-bold">Studio professionale</p>
                <p className="text-xs text-slate-500">Fisioterapia & Osteopatia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SMART RECOVERY SUITE™ — Premium clinical features ================= */}
      <section className="py-20 bg-slate-950 text-white" data-testid="wellness-suite-section">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-500 to-emerald-500 text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-lg shadow-cyan-500/30">
              <Crown className="w-3 h-3" /> Recovery Suite · Premium
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight mt-4">
              Non è un gestionale.<br />
              È <span className="bg-gradient-to-r from-cyan-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">la piattaforma clinica</span> del tuo studio.
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto mt-4 text-base sm:text-lg">
              Otto tecnologie pensate per fisioterapisti, osteopati e centri posturali.
              Per percepire ogni paziente come un percorso di recupero — dalla mappa del dolore
              fino al Patient Portal mobile premium.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              {
                icon: Activity,
                badge: "01",
                title: "Smart Body Map™",
                desc: "Mappa interattiva front + back del corpo con 24 zone cliniche. Dolore, rigidità, infiammazione, mobilità, trigger points e note operative — tutto color-coded e timestamped.",
                highlights: ["24 zone anatomiche", "Front + Back view", "Audit log per ogni modifica"],
                grad: "from-cyan-500 to-blue-600",
              },
              {
                icon: Heart,
                badge: "02",
                title: "Smart Recovery Timeline™",
                desc: "Timeline visuale del recupero del paziente: prima visita, sedute, miglioramenti, ricadute. Tutto in un'unica vista premium che il paziente capisce a colpo d'occhio.",
                highlights: ["Eventi clinici visuali", "Progress tracking", "Esportabile in PDF"],
                grad: "from-emerald-500 to-teal-500",
              },
              {
                icon: Camera,
                badge: "03",
                title: "Smart Posture™",
                desc: "Foto posturali front/lato/dietro con slider before/after drag-and-drop. Il paziente vede i progressi: il valore percepito esplode. AI Vision hooks pronti per il Q4 2026.",
                highlights: ["Slider comparativo premium", "4 tipi di assessment", "AI Vision-ready"],
                grad: "from-amber-500 to-orange-500",
              },
              {
                icon: Dumbbell,
                badge: "04",
                title: "Smart Exercise™",
                desc: "Catalogo curato di 24 esercizi in 8 categorie cliniche. Routine builder con sets/reps/hold personalizzati e link paziente condivisibile su WhatsApp.",
                highlights: ["24 esercizi · 8 categorie", "Link paziente WhatsApp", "Adherence tracking live"],
                grad: "from-violet-500 to-fuchsia-500",
              },
              {
                icon: Smartphone,
                badge: "05",
                title: "Smart Patient Portal™",
                desc: "Magic-link mobile-first stile healthtech: Recovery Score animato, heatmap costanza stile GitHub, conferma appuntamenti, documenti scaricabili. Niente app da installare.",
                highlights: ["Recovery Score 0-100", "Heatmap 12 settimane", "White-label studio"],
                grad: "from-teal-500 to-cyan-500",
              },
              {
                icon: Bell,
                badge: "06",
                title: "Push Notifications PWA",
                desc: "Notifiche W3C standard sul telefono del paziente: nuova routine assegnata, promemoria seduta, messaggio diretto dallo studio. Funziona anche con app chiusa.",
                highlights: ["Standard W3C nativo", "Deep-link al portale", "Auto-cleanup scadute"],
                grad: "from-rose-500 to-pink-500",
              },
              {
                icon: BarChart3,
                badge: "07",
                title: "Pain Score Tracking",
                desc: "Grafico premium dell'evoluzione del dolore (VAS 0-10). Il paziente vede i progressi. Tu hai sotto controllo l'efficacia del trattamento.",
                highlights: ["Sparkline elegante", "Confronto pre/post", "Storico illimitato"],
                grad: "from-rose-600 to-orange-500",
              },
              {
                icon: Target,
                badge: "08",
                title: "Pacchetti Sedute Smart",
                desc: "Crea pacchetti con scadenza e prezzo. Ogni seduta scarica il residuo. Ricevuta PDF generata via Smart Billing™ integrato.",
                highlights: ["Tracking residuo live", "Auto-billing integrato", "Reminder scadenza"],
                grad: "from-indigo-500 to-violet-500",
              },
            ].map((p, i) => (
              <div
                key={p.title}
                className="rounded-3xl bg-gradient-to-br from-slate-900 to-slate-950 border border-slate-800/80 p-6 hover:border-cyan-500/50 transition-all"
                data-testid={`wellness-suite-pillar-${i}`}
              >
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${p.grad} flex items-center justify-center shadow-lg mb-4`}>
                  <p.icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-[9px] font-bold uppercase tracking-[0.2em] text-cyan-300/80">{p.badge}</span>
                <h3 className="text-lg font-bold text-white mt-1">{p.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mt-2">{p.desc}</p>
                <ul className="mt-4 space-y-1.5">
                  {p.highlights.map((h, j) => (
                    <li key={j} className="flex items-start gap-2 text-xs text-slate-300">
                      <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Trust strip */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            <ShieldCheck className="w-4 h-4 text-emerald-500" />
            Conforme GDPR · Audit log completo · Dati medici crittografati · Backup automatico
          </div>

          {/* Brochure download */}
          <div className="mt-12 rounded-3xl bg-gradient-to-br from-teal-900/30 via-slate-900/60 to-emerald-900/30 border border-teal-500/20 backdrop-blur p-6 sm:p-8 max-w-4xl mx-auto" data-testid="brochure-cta-block">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-emerald-500 flex items-center justify-center shadow-lg shadow-teal-500/30">
                <FileText className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal-300">Brochure PDF · 7 pagine premium</p>
                <h3 className="text-xl sm:text-2xl font-black text-white mt-1">Scarica la brochure per fisioterapisti e osteopati</h3>
                <p className="text-sm text-slate-400 mt-2">
                  Tutta la Recovery Suite in una brochure A4 stampabile: Body Map, Recovery Timeline,
                  Smart Posture, Smart Exercise, Patient Portal con push notifications, Smart Consents
                  Wellness e ROI atteso per studio singolo.
                </p>
                <div className="flex flex-wrap gap-2 mt-4">
                  <a
                    href={`${API}/downloads/wellness-brochure.pdf`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 text-white text-sm font-bold hover:opacity-90 transition shadow-lg shadow-teal-500/30"
                    data-testid="download-wellness-brochure"
                  >
                    <Download className="w-4 h-4" /> Scarica PDF gratis
                  </a>
                  <button
                    onClick={() => {
                      const url = `${window.location.origin}/api/downloads/wellness-brochure.pdf`;
                      const text = `Ho appena scoperto SmartBooker Wellness: la piattaforma per fisioterapisti e osteopati. Brochure premium qui 👉 ${url}`;
                      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-teal-500/40 text-teal-300 text-sm font-bold hover:bg-teal-500/10 transition"
                    data-testid="share-wellness-brochure-wa"
                  >
                    <MessageSquare className="w-4 h-4" /> Condividi su WhatsApp
                  </button>
                  <button
                    onClick={async () => {
                      const url = `${window.location.origin}/api/downloads/wellness-brochure.pdf`;
                      try {
                        await navigator.clipboard.writeText(url);
                        // simple feedback — no toast lib on this page
                        const btn = document.querySelector('[data-testid="copy-brochure-link"]');
                        if (btn) { const o = btn.innerText; btn.innerText = "✓ Copiato"; setTimeout(() => btn.innerText = o, 1500); }
                      } catch (e) { console.warn("copy failed", e); }
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-700 text-slate-300 text-sm font-bold hover:bg-slate-800/50 transition"
                    data-testid="copy-brochure-link"
                  >
                    <ArrowRight className="w-4 h-4" /> Copia link
                  </button>
                </div>
                <p className="text-[10px] text-slate-500 mt-3">PDF A4 · 485 KB · stampabile in alta qualità · senza watermark</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SMART RECORDS — Premium feature ================= */}
      <SmartRecordsLandingSection
        accent="#10B981"
        demoPath="/demo-benessere-posturale"
        badge="Studio Fisio · Osteo · Paziente in cura"
        clientName="Marco Verdi"
        sectorTagline="SmartBooker non è solo agenda. È la cartella clinica fisioterapica del tuo studio:"
        highlights={["Problematiche · zona dolore · VAS", "Sedute · evoluzione VAS pre/post", "Esercizi a domicilio assegnati", "Referti e documenti", "Recupero progressi", "AI summary clinico"]}
        stats={[{ label: "Sedute", value: "5/10" }, { label: "VAS", value: "3/10 ↓" }, { label: "Spesa", value: "€275" }]}
        timeline={["Tecar + manipolazione L4-L5 · VAS 5→3", "Esercizi McKenzie + core · domiciliari", "Programma in netto miglioramento"]}
        aiHint="Aderenza esercizi alta. Stima dimissione tra 3-4 sedute."
      />

      {/* ================= PRICING ================= */}
      <section className="py-20 sm:py-28 px-5" data-testid="pricing-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">Scegli il piano giusto per il tuo studio</h2>
            <p className="text-slate-600 text-lg">Prezzo trasparente, zero sorprese, zero costi nascosti.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {/* ---------- Smart Growth ---------- */}
            <div className="relative rounded-3xl bg-white border-2 border-slate-200 p-8 shadow-sm hover:shadow-lg transition-shadow" data-testid="pricing-card-growth">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-[10px] font-black uppercase tracking-wider mb-2">Smart Growth</span>
                  <h3 className="text-2xl font-black">Smartbooker Growth</h3>
                  <p className="text-xs text-slate-500 mt-1">Tutto ciò che ti serve per lo studio.</p>
                </div>
                <Award className="w-10 h-10 text-slate-400" />
              </div>
              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-5xl font-black text-slate-900">€89</span>
                <span className="text-slate-500 font-semibold">/ mese</span>
              </div>
              <ul className="space-y-3 mb-8">
                {[
                  "Prenotazioni online illimitate",
                  "Agenda smart multi-operatore",
                  "Reminder WhatsApp automatici",
                  "Smart Site professionale incluso",
                  "Clienti illimitati",
                  "Supporto italiano via email + chat",
                  "Nessun costo nascosto, disdici quando vuoi",
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                    <span className="text-sm font-medium text-slate-700">{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => pricingClick("growth")}
                variant="outline"
                className="w-full rounded-full py-6 text-base font-bold border-2 border-slate-300 hover:bg-slate-50"
                data-testid="pricing-cta"
              >
                Attiva demo gratuita
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-center text-xs text-slate-500 mt-4">Meno di una seduta a settimana.</p>
            </div>

            {/* ---------- Benessere PRO ---------- */}
            <div className="relative rounded-3xl bg-gradient-to-br from-sky-600 via-emerald-600 to-teal-600 p-[2px] shadow-2xl shadow-emerald-500/30" data-testid="pricing-card-pro">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 text-white text-[10px] font-black uppercase tracking-wider shadow-lg">
                  ★ Il più Acquistato
                </span>
              </div>
              <div className="bg-white rounded-[22px] p-8 relative overflow-hidden">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-gradient-to-br from-sky-500/10 to-emerald-500/10 blur-2xl" />
                <div className="flex items-center justify-between mb-6 relative">
                  <div>
                    <span className="inline-block px-3 py-1 rounded-full bg-gradient-to-r from-sky-500 to-emerald-500 text-white text-[10px] font-black uppercase tracking-wider mb-2">Benessere PRO</span>
                    <h3 className="text-2xl font-black">Smartbooker Benessere PRO</h3>
                    <p className="text-xs text-slate-500 mt-1">Tutto di Growth + automazioni avanzate.</p>
                  </div>
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center shadow-lg">
                    <Heart className="w-6 h-6 text-white" fill="currentColor" />
                  </div>
                </div>
                <div className="flex items-baseline gap-2 mb-6 relative">
                  <span className="text-6xl font-black text-slate-900">€179</span>
                  <span className="text-slate-500 font-semibold">/ mese</span>
                </div>
                <ul className="space-y-3 mb-8 relative">
                  {[
                    { t: "Tutto incluso nel piano Growth", bold: true },
                    { t: "Smart Records · Cartella clinica AI", premium: true },
                    { t: "Pacchetti sedute con gestione saldo automatica" },
                    { t: "Recall pazienti dormienti (3 / 6 mesi)" },
                    { t: "Pagamenti online con Stripe integrati" },
                    { t: "Report mensili con KPI di studio" },
                    { t: "Account Manager dedicato" },
                    { t: "Onboarding 1:1 personalizzato" },
                  ].map((f, i) => (
                    <li key={i} className={`flex items-start gap-2.5 ${f.premium ? "rounded-lg bg-gradient-to-r from-violet-50 to-cyan-50 border border-violet-200/60 px-2 py-1.5 -mx-1" : ""}`}>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${f.premium ? "bg-gradient-to-br from-violet-500 to-cyan-500" : "bg-gradient-to-br from-sky-500 to-emerald-500"}`}>
                        {f.premium ? <Crown className="w-3 h-3 text-white" /> : <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                      </div>
                      <span className={`text-sm ${f.bold ? "font-bold text-slate-900" : f.premium ? "font-bold text-slate-800" : "font-medium text-slate-700"}`}>
                        {f.t}
                        {f.premium && <span className="ml-1.5 inline-flex items-center text-[8px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-violet-600 text-white align-middle">PREMIUM</span>}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => pricingClick("benessere_pro")}
                  className="w-full rounded-full py-7 text-base font-bold bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white shadow-xl shadow-emerald-500/40 hover:scale-[1.01] transition-all relative"
                  data-testid="pricing-cta-pro"
                >
                  Attiva Benessere PRO
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
                <p className="text-center text-xs text-slate-500 mt-4">14 giorni gratis · Nessuna carta richiesta</p>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-slate-500 mt-8">
            Tutti i piani includono <strong>14 giorni di prova gratuita</strong>. Puoi disdire quando vuoi.
          </p>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="py-20 sm:py-24 px-5 bg-slate-50" data-testid="faq-section">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mb-3">Domande frequenti</h2>
            <p className="text-slate-600">Tutto quello che devi sapere prima di provarlo.</p>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-200 overflow-hidden transition-all hover:border-slate-300" data-testid={`faq-${i}`}>
                <button
                  onClick={() => { const isOpen = openFaq === i; setOpenFaq(isOpen ? -1 : i); if (!isOpen) track("faq_open", { q: f.q }); }}
                  className="w-full px-5 py-4 flex items-center justify-between gap-4 text-left"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-bold text-slate-900">{f.q}</span>
                  <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="relative py-24 sm:py-32 px-5 overflow-hidden" data-testid="final-cta-section">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-600 via-teal-600 to-emerald-600" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.12),transparent_50%)]" />
        <div className="relative max-w-3xl mx-auto text-center text-white">
          <Sparkles className="w-10 h-10 mx-auto mb-5 opacity-80" />
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-4 leading-[1.1]">
            Dedica più tempo ai pazienti.<br/>
            <span className="opacity-80">Al resto pensa SmartBooker.</span>
          </h2>
          <p className="text-white/90 text-lg sm:text-xl mb-10 max-w-xl mx-auto">
            14 giorni gratis. Niente carta di credito. Attivo in pochi minuti.
          </p>
          <Button
            onClick={() => goTrial("final")}
            className="rounded-full px-10 py-8 text-lg font-bold bg-white text-slate-900 hover:bg-slate-100 shadow-2xl hover:scale-[1.03] transition-all"
            data-testid="final-cta"
          >
            Richiedi demo gratuita
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* ================= STICKY MOBILE CTA ================= */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 p-3 bg-white/95 backdrop-blur-md border-t border-slate-200 shadow-2xl" data-testid="sticky-mobile-cta">
        <Button
          onClick={() => goTrial("sticky-mobile")}
          className="w-full rounded-full py-6 text-base font-bold bg-gradient-to-r from-sky-500 to-emerald-500 hover:from-sky-600 hover:to-emerald-600 text-white shadow-xl"
          data-testid="sticky-cta"
        >
          Prova demo gratuita <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
      <div className="h-20 md:hidden" />

      {/* ================= FOOTER ================= */}
      <footer className="border-t border-slate-200 py-8 px-5 text-center text-xs text-slate-500 bg-white">
        <p>© 2026 SmartBooker — Software per fisioterapisti e osteopati</p>
        <p className="mt-1">
          <Link to="/privacy-policy" className="hover:text-slate-700">Privacy</Link> · {" "}
          <Link to="/terms-and-conditions" className="hover:text-slate-700">Termini</Link> · {" "}
          <Link to="/" className="hover:text-slate-700">Tutti i settori</Link>
        </p>
      </footer>
    </div>
  );
};

export default BenesserePosturaleLanding;
